import { useState } from 'react';
import { useProducts } from '@/hooks/useProducts';
import { useMovements } from '@/hooks/useInventory';
import { useBranch } from '@/context/BranchContext';
import { formatMoney, formatDateTime } from '@/lib/format';
import ProductImage from '@/components/feature/ProductImage';
import { MOVEMENT_LABELS, getStockStatus, STOCK_STATUS_META } from '@/lib/types';
import InventoryAdjustModal from '@/pages/inventory/components/InventoryAdjustModal';
import type { InventoryMovement, StockStatus as StockState } from '@/lib/types';

type Tab = 'stock' | 'movements';
type AdjustType = 'ajuste' | 'entrada' | 'salida';
type StockFilter = 'todos' | StockState;

function StockStatus({ stock, minStock }: { stock: number; minStock: number }) {
  const status = getStockStatus(stock, minStock);
  const meta = STOCK_STATUS_META[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.badge}`}>
      <i className={`${meta.icon} ${meta.dot}`} /> {meta.label}
    </span>
  );
}

const STOCK_FILTERS: { key: StockFilter; label: string }[] = [
  { key: 'todos', label: 'Todos' },
  { key: 'en_stock', label: 'En stock' },
  { key: 'bajo', label: 'Stock bajo' },
  { key: 'agotado', label: 'Agotados' },
];

export default function InventoryPage() {
  const { branches, activeBranchId, activeBranch } = useBranch();
  const [tab, setTab] = useState<Tab>('stock');
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState<StockFilter>('todos');
  const [modal, setModal] = useState<AdjustType | null>(null);
  const { products, loading, error, hasMore, loadMore, reload } = useProducts({ search, branchId: activeBranchId });
  const {
    movements,
    loading: movLoading,
    error: movError,
    hasMore: movHasMore,
    loadMore: movLoadMore,
    reload: reloadMov,
  } = useMovements(activeBranchId);

  const visibleProducts = products.filter((p) =>
    stockFilter === 'todos' ? true : getStockStatus(p.stock, p.min_stock) === stockFilter
  );

  const refreshAll = () => {
    reload();
    reloadMov();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Inventario</h1>
          <p className="mt-1 text-foreground-600 flex items-center gap-2">
            <i className="ri-building-2-line" /> {activeBranch ? activeBranch.name : 'Todas las sucursales'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setModal('ajuste')}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-edit-line" /> Ajustar inventario
          </button>
          <button
            onClick={() => setModal('entrada')}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 hover:bg-accent-600 px-3 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-arrow-down-circle-line" /> Entrada
          </button>
          <button
            onClick={() => setModal('salida')}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-arrow-up-circle-line" /> Salida
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 inline-flex items-center rounded-full bg-background-100 p-1 border border-background-200">
        <button
          onClick={() => setTab('stock')}
          className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
            tab === 'stock' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
          }`}
        >
          Existencias
        </button>
        <button
          onClick={() => setTab('movements')}
          className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
            tab === 'movements' ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
          }`}
        >
          Movimientos
        </button>
      </div>

      {tab === 'stock' ? (
        <>
          <div className="mt-5 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto..."
              className="w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {STOCK_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setStockFilter(f.key)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
                  stockFilter === f.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="mt-10 flex items-center justify-center py-16">
              <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : visibleProducts.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
              <i className="ri-archive-line text-4xl text-foreground-400" />
              <p className="mt-3 text-foreground-600">
                {stockFilter === 'agotado'
                  ? 'No hay productos agotados. ¡Bien!'
                  : 'No hay productos que coincidan.'}
              </p>
            </div>
          ) : (
            <>
              <div className="mt-5 rounded-lg border border-background-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-background-100 text-foreground-600 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium">Producto</th>
                      <th className="px-4 py-3 font-medium">SKU</th>
                      <th className="px-4 py-3 font-medium">Código de barra</th>
                      <th className="px-4 py-3 font-medium text-right">Stock actual</th>
                      <th className="px-4 py-3 font-medium text-right">Stock mínimo</th>
                      <th className="px-4 py-3 font-medium text-right">Precio</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {visibleProducts.map((p) => (
                      <tr key={p.id} className="hover:bg-background-100 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <ProductImage
                              url={p.image_url}
                              name={p.name}
                              className="w-10 h-10 rounded-md object-cover bg-background-100 shrink-0"
                              placeholderClassName="w-10 h-10 rounded-md shrink-0 p-0"
                              iconClassName="text-base"
                              showName={false}
                            />
                            <span className="font-medium text-foreground-950">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-foreground-700">{p.sku || '—'}</td>
                        <td className="px-4 py-3 text-foreground-700">{p.barcode || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-foreground-950">{p.stock}</td>
                        <td className="px-4 py-3 text-right text-foreground-700">{p.min_stock ?? 0}</td>
                        <td className="px-4 py-3 text-right text-foreground-900">{formatMoney(Number(p.sale_price))}</td>
                        <td className="px-4 py-3">
                          <StockStatus stock={p.stock} minStock={p.min_stock ?? 0} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="mt-5 flex justify-center">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-arrow-down-line" /> Cargar más
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {movError && (
            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
              {movError}
            </div>
          )}

          {movLoading ? (
            <div className="mt-10 flex items-center justify-center py-16">
              <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : movements.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
              <i className="ri-history-line text-4xl text-foreground-400" />
              <p className="mt-3 text-foreground-600">Aún no hay movimientos de inventario.</p>
            </div>
          ) : (
            <>
              <div className="mt-5 rounded-lg border border-background-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-background-100 text-foreground-600 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium">Producto</th>
                      <th className="px-4 py-3 font-medium">Tipo</th>
                      <th className="px-4 py-3 font-medium text-right">Cantidad</th>
                      <th className="px-4 py-3 font-medium text-right">Stock anterior</th>
                      <th className="px-4 py-3 font-medium text-right">Stock nuevo</th>
                      <th className="px-4 py-3 font-medium">Motivo</th>
                      <th className="px-4 py-3 font-medium">Usuario</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {movements.map((m: InventoryMovement) => (
                      <tr key={m.id} className="hover:bg-background-100 transition">
                        <td className="px-4 py-3 font-medium text-foreground-950">
                          {m.product?.name || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              m.type === 'venta'
                                ? 'bg-primary-100 text-primary-700'
                                : m.type === 'entrada'
                                  ? 'bg-accent-100 text-accent-900'
                                  : 'bg-secondary-100 text-secondary-900'
                            }`}
                          >
                            {MOVEMENT_LABELS[m.type] || m.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${m.quantity > 0 ? 'text-accent-700' : 'text-foreground-900'}`}>
                            {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-foreground-700">{m.previous_stock}</td>
                        <td className="px-4 py-3 text-right font-medium text-foreground-950">{m.new_stock}</td>
                        <td className="px-4 py-3 text-foreground-700">{m.reason || '—'}</td>
                        <td className="px-4 py-3 text-foreground-700">{m.user_name || '—'}</td>
                        <td className="px-4 py-3 text-foreground-600">{formatDateTime(m.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {movHasMore && (
                <div className="mt-5 flex justify-center">
                  <button
                    onClick={movLoadMore}
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-arrow-down-line" /> Cargar más
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {modal && (
        <InventoryAdjustModal
          open={!!modal}
          onClose={() => setModal(null)}
          type={modal}
          onDone={refreshAll}
          branchId={activeBranchId ?? branches[0]?.id ?? null}
        />
      )}
    </div>
  );
}